import React, { PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { makeStyles } from '@material-ui/core'

type PlacementType =
    | 'top-start'
    | 'top'
    | 'bottom-start'
    | 'bottom'
    | 'bottom-left'
    | 'bottom-right'
    | 'right'
    | 'left'
const VIEWPORT_MARGIN = 8

interface PositionInterface {
    top: number
    left: number
}

interface PropsInterface {
    open: boolean
    anchorEl: HTMLElement | null
    placement?: PlacementType
    offset?: number
    className?: string
    childrenClassName?: string
    onClose?(): void
    'data-testid'?: string
}

const getPosition = (
    anchorEl: HTMLElement,
    contentEl: HTMLDivElement,
    placement: PlacementType,
    offset: number
): PositionInterface => {
    const anchorRect = anchorEl.getBoundingClientRect()
    const contentRect = contentEl.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const contentWidth = Math.min(contentRect.width, viewportWidth - (VIEWPORT_MARGIN * 2))
    const contentHeight = Math.min(contentRect.height, viewportHeight - (VIEWPORT_MARGIN * 2))

    const getLeft = () => {
        switch (placement) {
                case 'top':
                case 'bottom':
                    return anchorRect.left + (anchorRect.width / 2) - (contentWidth / 2)
                case 'bottom-left':
                    return anchorRect.left - (anchorRect.width / 2) - (contentWidth / 2)
                case 'bottom-right':
                    return anchorRect.left + (anchorRect.width / 2)
                case 'right': {
                    const rightPosition = anchorRect.right + offset
                    const leftPosition = anchorRect.left - offset - contentWidth
                    const fitsRight = rightPosition + contentWidth <= viewportWidth - VIEWPORT_MARGIN
                    const fitsLeft = leftPosition >= VIEWPORT_MARGIN

                    return fitsRight || !fitsLeft ? rightPosition : leftPosition
                }
                case 'left': {
                    const leftPosition = anchorRect.left - offset - contentWidth
                    const rightPosition = anchorRect.right + offset
                    const fitsLeft = leftPosition >= VIEWPORT_MARGIN
                    const fitsRight = rightPosition + contentWidth <= viewportWidth - VIEWPORT_MARGIN

                    return fitsLeft || !fitsRight ? leftPosition : rightPosition
                }
                case 'bottom-start':
                case 'top-start':
                default:
                    return anchorRect.left
        }
    }

    const getTop = () => {
        if (placement === 'right' || placement === 'left') {
            return anchorRect.top + (anchorRect.height / 2) - (contentHeight / 2)
        }

        const preferredTop = placement.startsWith('top')
        const topPosition = anchorRect.top - offset - contentHeight
        const bottomPosition = anchorRect.bottom + offset
        const fitsTop = topPosition >= VIEWPORT_MARGIN
        const fitsBottom = bottomPosition + contentHeight <= viewportHeight - VIEWPORT_MARGIN

        return preferredTop
            ? (fitsTop || !fitsBottom ? topPosition : bottomPosition)
            : (fitsBottom || !fitsTop ? bottomPosition : topPosition)
    }

    return {
        top: Math.min(
            Math.max(getTop(), VIEWPORT_MARGIN),
            viewportHeight - contentHeight - VIEWPORT_MARGIN
        ),
        left: Math.min(
            Math.max(getLeft(), VIEWPORT_MARGIN),
            viewportWidth - contentWidth - VIEWPORT_MARGIN
        )
    }
}

const jss = () => {
    return {
        popover: {
            position: 'fixed',
            zIndex: 1300,
            pointerEvents: 'none'
        },
        content: {
            width: 'max-content',
            maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
            maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
            overflow: 'auto',
            boxSizing: 'border-box',
            pointerEvents: 'auto'
        }
    }
}

const useStyles = makeStyles(jss)

/**
 *
 */
const Popover = ({
    open,
    anchorEl,
    placement = 'top-start',
    offset = 8,
    className,
    childrenClassName,
    children,
    onClose,
    'data-testid': dataTestId = 'popover'
}: PropsWithChildren<PropsInterface>): JSX.Element => {
    const styles = useStyles()
    const contentRef = useRef<HTMLDivElement | null>(null)
    const [position, setPosition] = useState<PositionInterface | null>(null)

    const updatePosition = useCallback(() => {
        if (!anchorEl || !contentRef.current) {
            setPosition(null)
            return
        }

        setPosition(getPosition(anchorEl, contentRef.current, placement, offset))
    }, [anchorEl, placement, offset])

    useLayoutEffect(() => {
        if (!open || !anchorEl) {
            setPosition(null)
            return
        }

        updatePosition()
    }, [open, anchorEl, updatePosition])

    useEffect(() => {
        if (!open || !anchorEl) return

        const handleScroll = () => updatePosition()
        const handleResize = () => updatePosition()

        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('scroll', handleScroll, true)
            window.removeEventListener('resize', handleResize)
        }
    }, [open, anchorEl, updatePosition])

    useEffect(() => {
        if (!open || !anchorEl || !contentRef.current || typeof ResizeObserver === 'undefined') return

        const resizeObserver = new ResizeObserver(() => updatePosition())
        resizeObserver.observe(anchorEl)
        resizeObserver.observe(contentRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [open, anchorEl, updatePosition])

    useEffect(() => {
        if (!open || !onClose) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [open, onClose])

    if (!open || !anchorEl || typeof document === 'undefined') {
        return <></>
    }

    return ReactDOM.createPortal(
        <div
            className={`${styles.popover} ${className || ''}`}
            style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                visibility: position ? 'visible' : 'hidden'
            }}
            data-testid={dataTestId}
        >
            <div
                ref={contentRef}
                className={`${styles.content} ${childrenClassName || ''}`}
                data-testid={`${dataTestId}-content`}
            >
                {children}
            </div>
        </div>,
        document.body
    )
}

export default Popover
